/**
 * Custom Side Cart drawer.
 *
 * Auto-opens on WooCommerce archive add-to-cart and removes a line via the
 * njamito_remove_cart_item endpoint (then asks WooCommerce to refresh
 * fragments). The header bag badge is owned by cartCount.ts, which stays out of
 * this module so the count also works when the drawer is switched off.
 *
 * Guarded on the drawer shell: when the Main Options toggle is off the markup is
 * absent and nothing below runs.
 */

const sideCart = document.querySelector<HTMLElement>(".njamito-side-cart");

if (sideCart) {
  const overlay = sideCart.querySelector<HTMLElement>(".njamito-side-cart__overlay");
  const closeBtn = sideCart.querySelector<HTMLElement>(".njamito-side-cart__close");
  const $ = (window as any).jQuery;
  const ajax = (window as any).njamito_ajax;

  // Mirrors the state hamburger.ts sets, so the bag can dismiss the mobile nav
  // without the two modules importing each other.
  const closeMobileNav = (): void => {
    const nav = document.getElementById("mobileNav");
    if (!nav || !nav.classList.contains("active")) return;

    nav.classList.remove("active");
    document.getElementById("menuOverlay")?.classList.remove("active");
    document.getElementById("menuButton")?.setAttribute("aria-expanded", "false");
    document.body.classList.remove("mobile-nav-open");
    document.documentElement.classList.remove("mobile-nav-open");
  };

  const openDrawer = (): void => {
    sideCart.classList.add("is-open");
    sideCart.setAttribute("aria-hidden", "false");
    document.body.classList.add("side-cart-open");
  };

  const closeDrawer = (): void => {
    sideCart.classList.remove("is-open");
    sideCart.setAttribute("aria-hidden", "true");
    document.body.classList.remove("side-cart-open");
  };

  // Close interactions.
  closeBtn?.addEventListener("click", closeDrawer);
  overlay?.addEventListener("click", closeDrawer);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeDrawer();
  });

  // Delegated clicks inside the drawer (contents get swapped by fragments).
  sideCart.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;

    // Continue Shopping -> just close. In the filled cart it is a link to the
    // shop; modified clicks (new tab/window) keep that behaviour.
    if (target.closest(".njamito-side-cart__continue")) {
      const me = e as MouseEvent;
      if (me.metaKey || me.ctrlKey || me.shiftKey || me.altKey || me.button !== 0) {
        return;
      }
      e.preventDefault();
      closeDrawer();
      return;
    }

    // Remove line.
    const removeBtn = target.closest<HTMLElement>(".njamito-side-cart__remove");
    if (removeBtn && ajax) {
      e.preventDefault();
      const key = removeBtn.getAttribute("data-cart-item-key");
      if (!key) return;

      removeBtn.classList.add("is-loading");
      const body = new FormData();
      body.append("cart_item_key", key);
      body.append("nonce", ajax.nonce);

      // WooCommerce wc-ajax endpoint (relative -> same origin -> shared cart session).
      // Built from WooCommerce's localised URL so it keeps the WPML language
      // prefix: a bare `/?wc-ajax=` runs in the default language and lets WCML
      // re-key the cart lines against the wrong translation.
      const params =
        (window as any).wc_cart_fragments_params || (window as any).wc_add_to_cart_params;
      const removeUrl = params?.wc_ajax_url
        ? String(params.wc_ajax_url).replace("%%endpoint%%", "njamito_remove_cart_item")
        : "/?wc-ajax=njamito_remove_cart_item";

      fetch(removeUrl, {
        method: "POST",
        body,
        credentials: "same-origin",
      })
        .then((r) => r.json())
        .then((res) => {
          if (res && res.success && $) {
            $(document.body).trigger("wc_fragment_refresh");
          } else {
            removeBtn.classList.remove("is-loading");
          }
        })
        .catch(() => removeBtn.classList.remove("is-loading"));
    }
  });

  // Header bag -> open the drawer instead of navigating to the cart page.
  // An empty cart page is a dead end, and redirecting the icon to the shop
  // instead would teach the wrong affordance: the bag has to keep meaning "your
  // cart" so it is still the way back once there is something in it. The drawer
  // has its own empty state, so it answers both cases in place.
  //
  // The <a href> is left intact as the no-JS fallback; only the click is taken.
  document.querySelectorAll<HTMLElement>(".icon--cart a").forEach((link) => {
    link.addEventListener("click", (e) => {
      // Let modified clicks (new tab/window, downloads) behave normally.
      const me = e as MouseEvent;
      if (me.metaKey || me.ctrlKey || me.shiftKey || me.altKey || me.button !== 0) {
        return;
      }
      e.preventDefault();
      // The mobile nav is a fixed drawer over the page; leaving it open would
      // bury the side cart behind it.
      closeMobileNav();
      openDrawer();
    });
  });

  // Other "open cart" links (the sticky cart bar, inc/sticky-cart-bar.php) ask
  // for the drawer too. Delegated, because the bar's links live inside a cart
  // fragment and are replaced on every cart change.
  document.addEventListener("click", (e) => {
    const link = (e.target as HTMLElement).closest("[data-niamito-open-cart]");
    if (!link) return;
    const me = e as MouseEvent;
    if (me.metaKey || me.ctrlKey || me.shiftKey || me.altKey || me.button !== 0) {
      return;
    }
    e.preventDefault();
    closeMobileNav();
    openDrawer();
  });

  // WooCommerce fragment events (jQuery custom events).
  if ($) {
    // `added_to_cart` is WooCommerce's own (archive/loop AJAX); the namespaced
    // one comes from singleProductAddToCart.ts, which avoids core's event so
    // core does not append its "View cart" link beside the button.
    $(document.body).on("added_to_cart niamito_added_to_cart", openDrawer);
  }
}
